/**
 * Unified operation logger for image processing.
 * Provides consistent formatting with timing, memory info, and engine details.
 */

/**
 * Memory statistics captured before/after an operation
 */
export type MemorySnapshot = {
    heapUsedMb: number
    heapTotalMb: number
    externalMb: number
    arrayBuffersMb: number
    rssMb: number
}

/**
 * Get current memory usage snapshot (Node.js only, returns zeros in browser)
 */
export function getMemorySnapshot(): MemorySnapshot {
    if (typeof process !== "undefined" && process.memoryUsage) {
        const mem = process.memoryUsage()
        const toMb = (bytes: number) => bytes / 1024 / 1024
        return {
            heapUsedMb: toMb(mem.heapUsed),
            heapTotalMb: toMb(mem.heapTotal),
            externalMb: toMb(mem.external),
            arrayBuffersMb: toMb(mem.arrayBuffers),
            rssMb: toMb(mem.rss),
        }
    }
    return {heapUsedMb: 0, heapTotalMb: 0, externalMb: 0, arrayBuffersMb: 0, rssMb: 0}
}

/**
 * Extract image dimensions from function arguments if present
 */
export function extractImageDimensions(args: unknown[]): string | null {
    for (const arg of args) {
        if (arg && typeof arg === "object" && "width" in arg && "height" in arg) {
            const img = arg as {width: number; height: number; channelLayout?: string}
            const channels = img.channelLayout ? ` ${img.channelLayout}` : ""
            return `${img.width}x${img.height}${channels}`
        }
    }
    return null
}

/**
 * Operation logger for image processing.
 * Provides consistent formatting with timing, memory info, and engine details.
 *
 * Shows RSS (Resident Set Size) rather than heap because:
 * - RSS includes native memory from Sharp/libvips and WebAssembly
 * - OOM killers (Cloud Run, Kubernetes) monitor RSS, not heap
 * - Image processing allocates large buffers outside V8 heap
 */
export class OperationLogger {
    private startTime: number = 0
    private rssBefore: number = 0

    /** Start timing an operation */
    start(): void {
        this.startTime = performance.now()
        if (typeof process !== "undefined" && process.memoryUsage) {
            this.rssBefore = process.memoryUsage().rss / 1024 / 1024
        }
    }

    /** Log the operation with all details */
    log(operation: string, width: number, height: number, details: string, engine: string): void {
        const duration = performance.now() - this.startTime

        // Fixed-width columns for alignment
        const prefix = "[ImageProcessing]"
        const opCol = operation.padEnd(18)
        const sizeCol = `${width}x${height}`.padEnd(14)
        const detailsCol = details.padEnd(32)
        const engineCol = engine.padEnd(20)
        const timeCol = `${duration.toFixed(1)}ms`.padStart(10)

        let memInfo = ""
        if (this.rssBefore > 0 && typeof process !== "undefined" && process.memoryUsage) {
            const rssNow = process.memoryUsage().rss / 1024 / 1024
            const rssDelta = rssNow - this.rssBefore
            const sign = rssDelta >= 0 ? "+" : ""
            memInfo = ` | rss: ${rssNow.toFixed(0)}MB (${sign}${rssDelta.toFixed(0)})`
        }

        // eslint-disable-next-line no-console
        console.log(`${prefix} ${opCol} ${sizeCol} ${detailsCol} ${engineCol} ${timeCol}${memInfo}`)
    }
}

/** Create a new operation logger instance and start timing */
export function createOpLogger(): OperationLogger {
    const logger = new OperationLogger()
    logger.start()
    return logger
}
